import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { Button, EcardSend, EcardSendFormData, LoadingOverlay } from '@dotcom/crown-ui';
import emailIllustration from '../../assets/ecard-send-email.svg';
import messageIllustration from '../../assets/ecard-send-message.svg';
import { AnimationPreviewDialog } from '../../components/animation-preview/animation-preview-dialog';
import { useAnimationPreview } from '../../components/animation-preview/hooks/use-animation-preview';
import { Layout } from '../../components/layout';
import { SEO } from '../../components/seo/seo';
import { useAnalyticsContext } from '../../context/analytics-context';
import { useAppContext } from '../../context/app-context';
import { useCardContext } from '../../context/card-context';
import { useInitializationDataContext } from '../../context/data-context';
import { DigitalAddress } from '../../global-types';
import { useShallow } from 'zustand/react/shallow';
import { useAddressStore } from '../../stores/address';
import { getMonolithUrl, removeBeforeUnloadEvent } from '../../utils';
import { EmailSplash } from '../preview/fragments';
import { EcardSendShell } from './components/ecard-send-shell/ecard-send-shell';
import { useEcardSend } from './hooks/use-ecard-send';
import { getEcardValidationRules } from './utils/get-ecard-validation-rules';
import styles from './ecard-editor.module.scss';

const EMPTY_DIGITAL_ADDRESS: DigitalAddress = {
  recipient_first_name: '',
  recipient_last_name: '',
  contact_info: '',
  sender_first_name: '',
  sender_last_name: '',
  sender_email: '',
  url: '',
  recipient_type_code: 'E',
};

const getDigitalAddressField = (field: keyof EcardSendFormData): keyof DigitalAddress => {
  switch (field) {
    case 'recipientFirstName':
      return 'recipient_first_name';
    case 'recipientLastName':
      return 'recipient_last_name';
    case 'recipientEmail':
      return 'contact_info';
    case 'senderFirstName':
      return 'sender_first_name';
    case 'senderLastName':
      return 'sender_last_name';
  }
};

export const EcardSendView = () => {
  const { t } = useTranslation();
  const {
    appState: { previewImages },
  } = useAppContext();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const { addressData, updateDigitalAddress } = useAddressStore(
    useShallow((state) => ({
      addressData: state.addressData,
      updateDigitalAddress: state.updateDigitalAddress,
    })),
  );
  const digitalAddress = addressData.digital as DigitalAddress | null;
  const {
    cardState: { coverPreviewUrl },
  } = useCardContext();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { trackDGPlayAnimation, trackReturnToEdit, trackShareEcardLink } = useAnalyticsContext();
  const send = useEcardSend();
  const isMessageSplash = send.showConfirmation && send.deliveryMethod === 'message';
  const isExitBlocked = send.isSendingMessage;
  const blocker = useBlocker(isExitBlocked);
  useEffect(() => {
    if (blocker.state === 'blocked') blocker.reset();
  }, [blocker]);
  const projectId = initializedData?.project_id ?? sessionStorage.getItem('lastProjectLoaded') ?? '';

  const { animationPreviewImages, closeAnimation, isAnimationDialogOpen, playAnimation } = useAnimationPreview({
    projectId,
    autoPlay: true,
    onPlay: trackDGPlayAnimation,
  });
  const cardPreviewUrl =
    coverPreviewUrl || previewImages[0]?.url || previewImages[0]?.dataURL || animationPreviewImages[0];
  const emailDraft = useRef<DigitalAddress>({ ...EMPTY_DIGITAL_ADDRESS, ...digitalAddress });
  emailDraft.current = { ...emailDraft.current, ...digitalAddress };
  const defaultEmailValues = useMemo(
    () => ({
      recipientFirstName: digitalAddress?.recipient_first_name ?? '',
      recipientLastName: digitalAddress?.recipient_last_name ?? '',
      recipientEmail: digitalAddress?.contact_info ?? '',
      senderFirstName: digitalAddress?.sender_first_name ?? '',
      senderLastName: digitalAddress?.sender_last_name ?? '',
    }),
    [digitalAddress],
  );
  const persistEmailField = useCallback(
    (field: keyof EcardSendFormData, value: string) => {
      emailDraft.current = {
        ...emailDraft.current,
        [getDigitalAddressField(field)]: value,
        recipient_type_code: 'E',
      };
      updateDigitalAddress(emailDraft.current);
    },
    [updateDigitalAddress],
  );
  const validationRules = useMemo(
    () => getEcardValidationRules((key, options) => `${options ? t(key, options) : t(key)}`, persistEmailField),
    [persistEmailField, t],
  );
  const onEmailSubmit = (form: EcardSendFormData) => {
    trackShareEcardLink('send-email');
    return send.sendEmail(form);
  };
  const onSendMessage = () => {
    trackShareEcardLink('send-message');
    return send.sendMessage();
  };
  const onCopyLinkAgain = () => {
    trackShareEcardLink('copy-link-again');
    return send.copyLinkAgain();
  };
  const onViewEcard = () => {
    trackShareEcardLink('view-ecard');
    void playAnimation();
  };
  const onKeepShopping = () => {
    trackShareEcardLink('keep-shopping');
  };
  const returnToEditor = () => {
    if (isExitBlocked) return;
    trackReturnToEdit();
    navigate({ pathname: `/card/customization/edit/${projectId}`, search });
  };
  const onEmailSplashContinue = () => {
    if (isExitBlocked) return;
    removeBeforeUnloadEvent();
    window.location.href = getMonolithUrl();
  };

  if (send.showConfirmation) {
    return (
      <>
        <EmailSplash
          onContinue={onEmailSplashContinue}
          onPlayAnimations={onViewEcard}
          onTrackKeepShopping={onKeepShopping}
          onCloseOverride={onEmailSplashContinue}
          shareUrl={isMessageSplash ? send.shareUrl : undefined}
          onCopyLink={onCopyLinkAgain}
          isCopying={send.isCopying}
        />
        {isAnimationDialogOpen && (
          <AnimationPreviewDialog
            isOpen={isAnimationDialogOpen}
            panelImages={animationPreviewImages}
            onClose={closeAnimation}
          />
        )}
      </>
    );
  }

  return (
    <Layout
      hideToolbar
      previewHeaderProps={{ onEditClick: returnToEditor }}
      onCloseOverride={isExitBlocked ? () => undefined : undefined}
    >
      <LoadingOverlay isLoading={send.isSendingMessage} title={`${t('ecardSendView.sendingMessage')}`} overlay />
      <SEO title={t('ecardSendView.title')} description={t('ecardSendView.seo')} />
      <EcardSendShell
        cardPreviewUrl={cardPreviewUrl}
        previewAlt={`${t('ecardSendView.previewAlt')}`}
        playAnimationLabel={`${t('previewView.playAnimation')}`}
        onPlayAnimation={() => void playAnimation()}
      >
        <fieldset className={styles['send-fields']} disabled={send.isBusy || send.confirmationPending}>
          <EcardSend
            className={
              send.deliveryMethod === 'message'
                ? `${styles['ecard-send']} ${styles['message-layout']}`
                : styles['ecard-send']
            }
            deliveryMethod={send.deliveryMethod}
            onDeliveryMethodChange={send.changeDeliveryMethod}
            onEmailSubmit={onEmailSubmit}
            shareUrl={send.shareUrl}
            onSendMessage={onSendMessage}
            defaultEmailValues={defaultEmailValues}
            validationRules={validationRules}
            isSendingMessage={send.isBusy}
            emailIllustration={<img className={styles.illustration} src={emailIllustration} alt="" />}
            messageIllustration={<img className={styles.illustration} src={messageIllustration} alt="" />}
          />
        </fieldset>
        {((send.deliveryMethod === 'message' && (send.preparationFailed || send.messageSendFailed)) ||
          send.confirmationPending) && (
          <Button
            variant="secondary"
            disabled={send.isBusy}
            onClick={() =>
              void (send.confirmationPending
                ? send.retryConfirmation()
                : send.preparationFailed
                ? send.prepareMessage()
                : send.sendMessage())
            }
          >
            {t('ecardSendView.retrySend')}
          </Button>
        )}
      </EcardSendShell>
      {isAnimationDialogOpen && (
        <AnimationPreviewDialog
          isOpen={isAnimationDialogOpen}
          panelImages={animationPreviewImages}
          onClose={closeAnimation}
        />
      )}
    </Layout>
  );
};
